import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash, FloppyDisk, FileXls, FilePdf, Printer, Receipt,
  Calendar, Buildings, MagnifyingGlass, ShoppingCart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const todayISO = () => new Date().toISOString().slice(0, 10);
const PRODUCTION_FOR = ["Breakfast", "Lunch", "Dinner", "Full Day", "Event",
  "Catering", "Festival", "Cloud Kitchen", "Banquet", "Bulk Order", "Staff Meal", "Other"];

export default function QuickPurchasesPage() {
  const { guard, isAdmin } = useAuth();
  const [tab, setTab] = useState("new");
  const [auto, setAuto] = useState({ outlets: [], vendors: [], brands: [], purchased_by: [] });
  const [purchases, setPurchases] = useState([]);
  const [activePurchaseId, setActivePurchaseId] = useState(null);

  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [productionFor, setProductionFor] = useState("Lunch");
  const [outlet, setOutlet] = useState("");
  const [brand, setBrand] = useState("");
  const [vendor, setVendor] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [purchasedBy, setPurchasedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [ingItems, setIngItems] = useState([]);
  const [miscItems, setMiscItems] = useState([]);
  const [totals, setTotals] = useState({ ingredient_value: 0, misc_value: 0, total_value: 0 });

  // Load autocomplete + list
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/quick-purchases/autocomplete").then(({ data }) => setAuto(data || {})).catch(() => {});
    refreshList();
  }, [isAdmin]); // eslint-disable-line

  const refreshList = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/quick-purchases");
      setPurchases(data || []);
    } catch (e) { /* ignore */ }
  };

  // Live compute totals
  useEffect(() => {
    if (!isAdmin || (ingItems.length === 0 && miscItems.length === 0)) {
      setTotals({ ingredient_value: 0, misc_value: 0, total_value: 0 });
      return;
    }
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.post("/quick-purchases/compute", {
          ingredient_items: ingItems, misc_items: miscItems,
        });
        setTotals(data.totals);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(tid);
  }, [ingItems, miscItems, isAdmin]);

  const addIngredient = () => setIngItems([...ingItems, { id: crypto.randomUUID(), name: "", unit: "kg", qty: 0, rate_per_kg: 0 }]);
  const addMisc = () => setMiscItems([...miscItems, { id: crypto.randomUUID(), name: "", unit: "unit", qty: 1, rate: 0 }]);
  const updIng = (i, patch) => setIngItems(ingItems.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const updMisc = (i, patch) => setMiscItems(miscItems.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const reset = () => {
    setActivePurchaseId(null);
    setPurchaseDate(todayISO()); setProductionFor("Lunch");
    setOutlet(""); setBrand(""); setVendor(""); setInvoiceNumber("");
    setPurchasedBy(""); setRemarks("");
    setIngItems([]); setMiscItems([]);
  };

  const hydrate = (p) => {
    setActivePurchaseId(p.id);
    setPurchaseDate(p.purchase_date || todayISO());
    setProductionFor(p.production_for || "Lunch");
    setOutlet(p.outlet || ""); setBrand(p.brand || "");
    setVendor(p.vendor_name || ""); setInvoiceNumber(p.invoice_number || "");
    setPurchasedBy(p.purchased_by || ""); setRemarks(p.remarks || "");
    setIngItems((p.ingredient_items || []).map((it) => ({ id: crypto.randomUUID(), ...it })));
    setMiscItems((p.misc_items || []).map((it) => ({ id: crypto.randomUUID(), ...it })));
    setTab("new");
    toast.success(`Loaded purchase from ${p.purchase_date}`);
  };

  const save = guard(async () => {
    if (ingItems.length === 0 && miscItems.length === 0) { toast.error("Add at least one line"); return; }
    const payload = {
      purchase_date: purchaseDate, production_for: productionFor,
      outlet, brand, vendor_name: vendor, invoice_number: invoiceNumber,
      purchased_by: purchasedBy, remarks,
      ingredient_items: ingItems, misc_items: miscItems,
    };
    try {
      let saved;
      if (activePurchaseId) {
        const { data } = await api.put(`/quick-purchases/${activePurchaseId}`, payload);
        saved = data; toast.success("Purchase updated");
      } else {
        const { data } = await api.post("/quick-purchases", payload);
        saved = data; toast.success("Purchase saved");
      }
      setActivePurchaseId(saved.id);
      refreshList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  }, "save purchase");

  const del = guard(async (id) => {
    if (!window.confirm("Delete this purchase?")) return;
    try { await api.delete(`/quick-purchases/${id}`); toast.success("Deleted"); refreshList(); }
    catch { toast.error("Delete failed"); }
  }, "delete purchase");

  const download = guard(async (url, fname) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
  }, "download");

  return (
    <div className="app-shell">
      <Header />
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
            <ShoppingCart size={14} weight="fill" /> Quick Purchases
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
            Record every spend, <br className="hidden md:block" /><span className="text-[#D4AF37]">no PO needed.</span>
          </h1>
          <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl">
            Capture daily market, cash, vendor and emergency purchases. Pulls into Inventory + Monthly Spend automatically.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-6">
        {!isAdmin && <div className="kostra-card p-6 text-center text-sm text-[#4B5563]">Admin login required.</div>}
        {isAdmin && (
          <Tabs value={tab} onValueChange={setTab} className="space-y-6">
            <TabsList className="bg-white border border-[#E5E7EB] p-1 h-auto rounded-lg">
              <TabsTrigger value="new" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-qp-new">
                {activePurchaseId ? "Edit Purchase" : "New Purchase"}
              </TabsTrigger>
              <TabsTrigger value="list" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-qp-list">
                History &amp; Reports
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="space-y-6 mt-0">
              <section className="kostra-card p-5 md:p-6" data-testid="qp-header-card">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <Field label="Purchase Date" col={3}>
                    <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="h-10 font-mono-num" data-testid="qp-date" />
                  </Field>
                  <Field label="Production For" col={3}>
                    <Select value={productionFor} onValueChange={setProductionFor}>
                      <SelectTrigger className="h-10" data-testid="qp-for"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRODUCTION_FOR.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Outlet" col={3}>
                    <Input value={outlet} onChange={(e) => setOutlet(e.target.value)} list="outlet-list" className="h-10" placeholder="e.g. Andheri W" data-testid="qp-outlet" />
                    <datalist id="outlet-list">{auto.outlets?.map((o) => <option key={o} value={o} />)}</datalist>
                  </Field>
                  <Field label="Brand" col={3}>
                    <Input value={brand} onChange={(e) => setBrand(e.target.value)} list="brand-list" className="h-10" data-testid="qp-brand" />
                    <datalist id="brand-list">{auto.brands?.map((o) => <option key={o} value={o} />)}</datalist>
                  </Field>
                  <Field label="Vendor" col={4}>
                    <Input value={vendor} onChange={(e) => setVendor(e.target.value)} list="vendor-list" className="h-10" placeholder="e.g. Sharma Stores" data-testid="qp-vendor" />
                    <datalist id="vendor-list">{auto.vendors?.map((o) => <option key={o} value={o} />)}</datalist>
                  </Field>
                  <Field label="Invoice #" col={3}>
                    <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="h-10 font-mono-num" data-testid="qp-invoice" />
                  </Field>
                  <Field label="Purchased By" col={3}>
                    <Input value={purchasedBy} onChange={(e) => setPurchasedBy(e.target.value)} list="user-list" className="h-10" data-testid="qp-purchased-by" />
                    <datalist id="user-list">{auto.purchased_by?.map((o) => <option key={o} value={o} />)}</datalist>
                  </Field>
                  <Field label="Remarks" col={2}>
                    <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="h-10" data-testid="qp-remarks" />
                  </Field>
                </div>
              </section>

              <section className="kostra-card p-5 md:p-6" data-testid="qp-ingredient-table">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="kostra-section-title text-lg">Ingredient Purchases</h3>
                  <Button onClick={addIngredient} variant="outline" className="h-9 text-xs" data-testid="qp-add-ing">
                    <Plus size={14} weight="bold" className="mr-1" /> Add Ingredient
                  </Button>
                </div>
                {ingItems.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-6 text-center text-xs text-[#4B5563]">No ingredient lines</div>
                ) : (
                  <table className="w-full text-sm" data-testid="qp-ing-rows">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Ingredient</th>
                        <th className="py-2 pr-2 w-24">Unit</th>
                        <th className="py-2 pr-2 w-24 text-right">Qty</th>
                        <th className="py-2 pr-2 w-28 text-right">Rate</th>
                        <th className="py-2 pr-2 w-28 text-right">Amount</th>
                        <th className="py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingItems.map((it, i) => (
                        <tr key={it.id} className="border-b border-[#F1F2F4]" data-testid={`qp-ing-${i}`}>
                          <td className="py-1.5 pr-2"><Input value={it.name} onChange={(e) => updIng(i, { name: e.target.value })} className="h-8 text-xs" placeholder="e.g. Chicken" data-testid={`qp-ing-name-${i}`} /></td>
                          <td className="py-1.5 pr-2"><Input value={it.unit} onChange={(e) => updIng(i, { unit: e.target.value })} className="h-8 text-xs" /></td>
                          <td className="py-1.5 pr-2"><Input type="number" min="0" step="0.1" value={it.qty} onChange={(e) => updIng(i, { qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" data-testid={`qp-ing-qty-${i}`} /></td>
                          <td className="py-1.5 pr-2"><Input type="number" min="0" step="1" value={it.rate_per_kg} onChange={(e) => updIng(i, { rate_per_kg: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" data-testid={`qp-ing-rate-${i}`} /></td>
                          <td className="py-1.5 pr-2 text-right font-mono-num text-xs font-semibold text-[#0A3B24]">{fmtINR(it.qty * it.rate_per_kg)}</td>
                          <td className="py-1.5"><button onClick={() => setIngItems(ingItems.filter((_, idx) => idx !== i))} className="text-[#DC2626] hover:bg-[#FBE8E8] rounded p-1" data-testid={`qp-ing-del-${i}`}><Trash size={12} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="kostra-card p-5 md:p-6" data-testid="qp-misc-table">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="kostra-section-title text-lg">Miscellaneous Purchases <span className="text-xs text-[#4B5563] font-normal">(gas, packaging, cleaning, etc.)</span></h3>
                  <Button onClick={addMisc} variant="outline" className="h-9 text-xs" data-testid="qp-add-misc">
                    <Plus size={14} weight="bold" className="mr-1" /> Add Item
                  </Button>
                </div>
                {miscItems.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-6 text-center text-xs text-[#4B5563]">No miscellaneous items</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Item</th>
                        <th className="py-2 pr-2 w-24">Unit</th>
                        <th className="py-2 pr-2 w-24 text-right">Qty</th>
                        <th className="py-2 pr-2 w-28 text-right">Rate</th>
                        <th className="py-2 pr-2 w-28 text-right">Amount</th>
                        <th className="py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {miscItems.map((it, i) => (
                        <tr key={it.id} className="border-b border-[#F1F2F4]" data-testid={`qp-misc-${i}`}>
                          <td className="py-1.5 pr-2"><Input value={it.name} onChange={(e) => updMisc(i, { name: e.target.value })} className="h-8 text-xs" placeholder="e.g. Gas Cylinder" data-testid={`qp-misc-name-${i}`} /></td>
                          <td className="py-1.5 pr-2"><Input value={it.unit} onChange={(e) => updMisc(i, { unit: e.target.value })} className="h-8 text-xs" /></td>
                          <td className="py-1.5 pr-2"><Input type="number" min="0" step="1" value={it.qty} onChange={(e) => updMisc(i, { qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" /></td>
                          <td className="py-1.5 pr-2"><Input type="number" min="0" step="1" value={it.rate} onChange={(e) => updMisc(i, { rate: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" /></td>
                          <td className="py-1.5 pr-2 text-right font-mono-num text-xs font-semibold text-[#0A3B24]">{fmtINR(it.qty * it.rate)}</td>
                          <td className="py-1.5"><button onClick={() => setMiscItems(miscItems.filter((_, idx) => idx !== i))} className="text-[#DC2626] hover:bg-[#FBE8E8] rounded p-1"><Trash size={12} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {(ingItems.length > 0 || miscItems.length > 0) && (
                <section className="grid grid-cols-3 gap-4" data-testid="qp-totals">
                  <Kpi label="Ingredient Value" value={fmtINR(totals.ingredient_value)} />
                  <Kpi label="Misc Value" value={fmtINR(totals.misc_value)} accent="#D4AF37" />
                  <Kpi label="Grand Total" value={fmtINR(totals.total_value)} accent="#0A3B24" highlight />
                </section>
              )}

              <section className="kostra-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="qp-actions">
                <div className="text-xs text-[#4B5563]">{activePurchaseId ? `Editing ${activePurchaseId.slice(0, 8)}…` : "New purchase"}</div>
                <div className="flex flex-wrap gap-2">
                  {activePurchaseId && <Button onClick={reset} variant="outline" className="h-10 text-xs" data-testid="qp-new">New</Button>}
                  <Button onClick={save} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-10" data-testid="qp-save">
                    <FloppyDisk size={14} weight="bold" className="mr-2" /> {activePurchaseId ? "Update Purchase" : "Save Purchase"}
                  </Button>
                  {activePurchaseId && (
                    <Button onClick={() => download(`/quick-purchases/${activePurchaseId}/export/excel`, `QuickPurchase_${purchaseDate.replace(/-/g, '')}.xlsx`)} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-10" data-testid="qp-dl-excel">
                      <FileXls size={14} weight="bold" className="mr-2" /> Download Sheet (Excel)
                    </Button>
                  )}
                  <Button onClick={() => window.print()} variant="outline" className="h-10 text-xs" data-testid="qp-print">
                    <Printer size={14} weight="bold" className="mr-2" /> Print
                  </Button>
                </div>
              </section>
            </TabsContent>

            <TabsContent value="list" className="space-y-6 mt-0">
              <section className="kostra-card p-5" data-testid="qp-downloads">
                <h3 className="kostra-section-title text-lg mb-3">Download Center</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Button onClick={() => download("/quick-purchases/reports/vendor/excel", `VendorPurchase_${todayISO().replace(/-/g, '')}.xlsx`)} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold" data-testid="qp-dl-vendor">
                    <FileXls size={14} weight="bold" className="mr-2" /> Vendor Report
                  </Button>
                  <Button onClick={() => download("/quick-purchases/reports/ingredient/excel", `IngredientPurchase_${todayISO().replace(/-/g, '')}.xlsx`)} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold" data-testid="qp-dl-ing">
                    <FileXls size={14} weight="bold" className="mr-2" /> Ingredient Report
                  </Button>
                  <Button onClick={() => download(`/quick-purchases/reports/monthly/excel?year=${new Date().getFullYear()}`, `MonthlyPurchaseSummary_${new Date().getFullYear()}.xlsx`)} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold" data-testid="qp-dl-monthly">
                    <FileXls size={14} weight="bold" className="mr-2" /> Monthly Summary
                  </Button>
                  <Button onClick={() => download("/quick-purchases/reports/register/pdf", `PurchaseRegister_${todayISO().replace(/-/g, '')}.pdf`)} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold" data-testid="qp-dl-register">
                    <FilePdf size={14} weight="bold" className="mr-2" /> Purchase Register (PDF)
                  </Button>
                </div>
              </section>

              <section className="kostra-card p-5" data-testid="qp-list">
                <h3 className="kostra-section-title text-lg mb-3">Purchases ({purchases.length})</h3>
                {purchases.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center text-sm text-[#4B5563]">No purchases yet</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Date</th>
                        <th className="py-2 pr-2">Vendor</th>
                        <th className="py-2 pr-2">Outlet</th>
                        <th className="py-2 pr-2">For</th>
                        <th className="py-2 pr-2 text-center">Lines</th>
                        <th className="py-2 pr-2 text-right">Total</th>
                        <th className="py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map((p) => (
                        <tr key={p.id} className="border-b border-[#F1F2F4] hover:bg-[#FDF9EE]/40" data-testid={`qp-list-row-${p.id}`}>
                          <td className="py-2 pr-2 font-mono-num text-[#0A3B24]">{p.purchase_date}</td>
                          <td className="py-2 pr-2 font-semibold">{p.vendor_name || "—"}</td>
                          <td className="py-2 pr-2 text-[#4B5563]">{p.outlet || "—"}</td>
                          <td className="py-2 pr-2 text-[#4B5563]">{p.production_for}</td>
                          <td className="py-2 pr-2 text-center font-mono-num">{p.totals?.line_count || 0}</td>
                          <td className="py-2 pr-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(p.totals?.total_value || 0)}</td>
                          <td className="py-2 text-right">
                            <button onClick={() => hydrate(p)} className="text-xs px-2 py-1 rounded bg-[#FDF9EE] text-[#0A3B24] hover:bg-[#F4E9C5] mr-1" data-testid={`qp-list-edit-${p.id}`}>Edit</button>
                            <button onClick={() => del(p.id)} className="text-xs px-2 py-1 rounded bg-[#FBE8E8] text-[#DC2626] hover:bg-[#F8C5C5]" data-testid={`qp-list-del-${p.id}`}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </TabsContent>
          </Tabs>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, col, children }) {
  return (
    <div className={`md:col-span-${col}`}>
      <Label className="kostra-data-label">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Kpi({ label, value, accent = "#111827", highlight }) {
  return (
    <div className={`kostra-card p-4 ${highlight ? "ring-2 ring-[#D4AF37]" : ""}`}>
      <div className="kostra-data-label text-[10px]">{label}</div>
      <div className="font-mono-num text-xl font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
